import { Upload, UploadCloud, X, XSquare } from "lucide-react";
import React, { Fragment, useEffect, useState } from "react";
import { Accept, useDropzone } from "react-dropzone";

import { Button } from "../ui/button";
import { Input } from "../ui/input";

import { isHttps } from "./FileUpload";
import { DialogDemo } from "./InputImage";

import pdfImg from "@/assets/images/icons/pdf.svg";
import { allowAllFileTypes, imgBaseUrl } from "@/constants/constants";
import { TAny } from "@/interfaces/globals";
import {
  convertImageFormat,
  fileImagePreviewer,
  splitImageFileName,
} from "@/lib/file-image.utils";
import { cn } from "@/lib/utils";
import Icons from "@/utils/Icons";
import { customToast } from "@/utils/custom-toast";

type ImageDropProps = {
  files: File[];
  setFiles: (files: File[]) => void;
  image?: string;
  type?: "input" | "box" | "imageBox";
  withCropper?: boolean;
  strictCrop?: boolean;
  aspect?: number;
  isFile?: boolean;
  customLabel?: string;
  fileName?: string;
  imageList?: string[];
  placeholder?: string;
  /** If true, allows both File objects and image URLs as input */
  acceptsMixedContent?: boolean;
};

type Timage = {
  file: File;
  preview: string;
  fileName?: string;
  // type?: "input" | "box" | "imageBox";
};

const SingleFileUpload: React.FC<ImageDropProps> = ({
  files,
  setFiles,
  image,
  type = "box",
  withCropper,
  strictCrop,
  isFile = false,
  customLabel,
  fileName,
  imageList,
  placeholder,
  acceptsMixedContent,
}) => {
  const [previewFile, setPreviewFile] = React.useState<TAny>();
  const [file, setFile] = useState<Timage | Timage[] | null | undefined>();
  const [openCrop, setOpenCrop] = React.useState(false);

  const getAcceptingTypes = (): Accept => {
    if (acceptsMixedContent) {
      return {
        "application/pdf": [],
        "image/*": [],
      };
    }
    if (isFile) {
      return {
        "application/pdf": [],
      };
    }

    return {
      "image/*": [],
    };
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: getAcceptingTypes(),
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles?.length === 0) {
        return;
      }
      //Check the maximum file size of 2MB
      if (acceptedFiles[0].size > 2 * 1024 * 1024) {
        customToast.error("File size should not exceed 2MB");

        return;
      }
      if (!allowAllFileTypes.includes(acceptedFiles[0].type.split("/")[1])) {
        customToast.error(
          "Invalid file format. Please upload a valid image file",
        );

        return;
      }
      const convertedFile = await convertImageFormat({
        file: acceptedFiles[0],
        targetFormat: "webp",
      });
      setFiles([convertedFile as File]);
      setFile({
        file: convertedFile as File,
        preview: URL?.createObjectURL(convertedFile as File),
        fileName: acceptedFiles[0].name,
      });
      setOpenCrop(true);
      setPreviewFile(
        acceptedFiles?.map((file) =>
          Object.assign(file, {
            preview: URL?.createObjectURL(file),
          }),
        ),
      );
    },
  });

  const [isImage, setIsImage] = React.useState(false);

  const handleCancel = (e: React.MouseEvent): void => {
    e.stopPropagation();
    setIsImage(false);
    setFiles([]);
    setPreviewFile([]);
  };

  useEffect(() => {
    setPreviewFile(
      typeof files !== "string" && files?.length
        ? files?.map((file) => ({
            preview: file,
            name:
              typeof file === "string" && isHttps(file)
                ? splitImageFileName(file, imgBaseUrl)
                : file?.name,
          }))
        : [],
    );
  }, [files]);

  // initially set isImage to true if image is present
  useEffect(() => {
    if (image?.length && image !== "null") {
      setIsImage(true);
    }
  }, [image, imageList]);

  // on reset button click set then image to default
  useEffect(() => {
    if (typeof files === "string" && files !== "null" && image !== "null") {
      setIsImage(true);
    }
  }, [files, image]);

  if (type === "input") {
    return (
      <div {...getRootProps()}>
        <div>
          <input {...getInputProps()} />
          {!previewFile?.length ? (
            <>
              {isImage ? (
                <div className='flex items-center gap-x-2 '>
                  <img
                    src={files.toString()}
                    alt='uploaded image'
                    className={cn("rounded-sm h-9 w-14")}
                  />
                  <Input
                    placeholder={"Click to replace file"}
                    className='cursor-pointer '
                    value={fileName || "Click to replace file"}
                    readOnly
                  />
                </div>
              ) : (
                <div>
                  <Input
                    placeholder='Upload a new File'
                    className='cursor-pointer '
                    readOnly
                  />
                </div>
              )}
            </>
          ) : (
            <div className='flex items-center gap-x-2'>
              {fileImagePreviewer({
                file: previewFile?.[0]?.preview,
                className: "rounded-sm",
                size: 50,
              })}
              <Input
                value={previewFile?.[0].name ?? fileName}
                readOnly
                className='cursor-pointer '
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (type === "imageBox") {
    return (
      <div {...getRootProps()}>
        <div>
          <input {...getInputProps()} />
          {!previewFile?.length ? (
            <>
              {isImage ? (
                <div className='flex justify-between p-1 border rounded-md h-11'>
                  <div className='flex items-center justify-center gap-2'>
                    <img
                      src={isFile ? pdfImg : image}
                      alt='uploaded image'
                      className='rounded-sm h-9 w-14'
                    />
                    <p className='text-sm truncate max-w-40 text-aldgray'>
                      {image ? splitImageFileName(image, imgBaseUrl) : ""}
                    </p>
                  </div>
                  <Button
                    className='text-red-500 bg-[#F9E0E0] h-8'
                    onClick={(e) => {
                      handleCancel(e);
                    }}
                  >
                    <X size={20} className='mr-2' color='red' />
                    Remove
                  </Button>
                </div>
              ) : (
                <div className='flex justify-between p-1 border rounded-md max-h-11'>
                  <div className='flex flex-1'>
                    <Input
                      icon={{ icon: UploadCloud, position: "left" }}
                      className='w-full h-8 text-xs border-0 active:border-0 focus:border-0 ring-0'
                      placeholder={placeholder ?? "Drop/Click here to Upload"}
                    />
                  </div>
                  <Button
                    variant={"secondary"}
                    className='h-8 py-0 text-aldgray'
                  >
                    <Upload size={18} className='mr-2' />
                    Upload
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className='flex justify-between p-1 border rounded-md h-11'>
              <div className='flex items-center justify-center gap-2 overflow-hidden text-wrap'>
                <img
                  src={isFile ? pdfImg : previewFile[0]?.preview}
                  alt='uploaded image'
                  className='rounded-sm h-9 w-14'
                />
                <p className='text-sm truncate max-w-40 text-aldgray'>
                  {previewFile[0]?.name}
                </p>
              </div>

              <Button
                className='text-red-500 bg-[#F9E0E0] h-8'
                onClick={(e) => {
                  handleCancel(e);
                }}
              >
                <X size={20} className='mr-2' color='red' />
                Remove
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }
  // allowedImageTypes.includes(previewFile[0]?.name.split(".")[1])
  const getSrc = (): string => {
    if (!previewFile) {
      return "";
    }
    if (withCropper && typeof previewFile === "string") {
      return previewFile;
    } else if (
      allowAllFileTypes.includes(previewFile[0]?.name?.split(".")[1])
    ) {
      return typeof previewFile[0]?.preview === "string"
        ? previewFile[0]?.preview
        : URL?.createObjectURL(previewFile[0]?.preview);
    } else {
      return pdfImg;
    }
  };

  const label = customLabel || (isFile ? "Upload File" : "Upload Image");

  return (
    <Fragment>
      {withCropper && file && (
        <DialogDemo
          image={file as Timage}
          strictCrop={strictCrop}
          open={openCrop}
          setOpenCrop={setOpenCrop}
          onhandleCrop={() => {
            setOpenCrop(!openCrop);
          }}
          addNewPicture={(file) => {
            const data = file as unknown as Timage;
            if (data) {
              if (data?.file) {
                setFiles([data.file]);
              }
              if (data?.preview) {
                setPreviewFile(data.preview);
              }
            } else {
              setFiles([]);
              setPreviewFile([]);
            }
          }}
          aspect={1}
          dimension={{
            height: 266,
            width: 266,
          }}
        />
      )}
      <div {...getRootProps()}>
        <div className='bg-[#F8F8F8]  py-4 rounded-md dashed-border border-2 border-[#D8D6DE] bg-background '>
          <input {...getInputProps()} />

          {!previewFile?.length ? (
            <React.Fragment>
              {isImage &&
              typeof image === "string" &&
              image?.startsWith("http") ? (
                <div className='flex items-center justify-between mx-4'>
                  <div className='flex items-center w-5/6 gap-2'>
                    <img
                      src={isFile ? pdfImg : image}
                      alt='Dropdown Image'
                      className='w-20 h-20 rounded-sm'
                    />
                    <p className='truncate'>
                      {splitImageFileName(image, imgBaseUrl)}
                    </p>
                  </div>
                  <XSquare
                    color='red'
                    className='relative z-[58] w-1/6 cursor-pointer'
                    onClick={(e) => {
                      handleCancel(e);
                    }}
                  />
                </div>
              ) : (
                <div className='flex flex-col items-center justify-center cursor-pointer'>
                  <Icons name='cloud-icon' color='#667085' size={28} />
                  <h2 className='font-normal text-base text-[#534F67]'>
                    {label}
                  </h2>
                  <p className='text-sm font-normal text-gray-400 '>
                    Drop file here or click
                    <span className='text-red-600'> browser</span> through your
                    device
                  </p>
                </div>
              )}
            </React.Fragment>
          ) : (
            <div className='flex items-center justify-between mx-4'>
              <div className='flex items-center justify-start w-5/6 gap-2 mx-4'>
                <img
                  src={isFile ? pdfImg : getSrc()}
                  alt='Dropdown Image'
                  className='w-20 h-20 rounded-sm'
                />
                <p className='truncate text-aldgray'>
                  {(file as Timage)?.fileName}
                </p>
              </div>
              <XSquare
                color='red'
                className='w-1/6 cursor-pointer'
                onClick={(e) => handleCancel(e)}
              />
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default SingleFileUpload;
